import React, { useEffect, useState } from "react";
import { Button, Modal, message, Select } from "antd";
import { Form, InputGroup, Badge } from "react-bootstrap";
import { PlusOutlined, EditOutlined, DeleteOutlined, PhoneOutlined, MailOutlined } from "@ant-design/icons";
import { oneKlicks } from "../../SDK/oneclick";
import { getuserdata } from "../../services/apiService";
import Paginationnew from "../../Uitiles/Paginationnew";

const BloodDonors = () => {
    const [donors, setDonors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingDonor, setEditingDonor] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    // Form states
    const [formData, setFormData] = useState({
        name: '',
        blood_group: '',
        phone: '',
        email: '',
        address: '',
        last_donation_date: '',
        status: 'active'
    });

    const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

    useEffect(() => {
        loadDonors();
    }, [page, searchText]);

    const loadDonors = async () => {
        try {
            setLoading(true);
            const userId = await getuserdata('id');
            const conditions = { blood_bank_id: userId };
            const search = searchText ? {
                name: searchText,
                phone: searchText,
                blood_group: searchText,
                email: searchText
            } : {};
            const pagination = { page, limit };

            const response = await oneKlicks.read('', 'blood_donors', conditions, 'id DESC', search, pagination);
            if (response && response.data) {
                setDonors(response.data);
                setTotalPages(response.totalPages || 1);
            }
        } catch (error) {
            console.error('Error loading donors:', error);
            message.error('Failed to load donors');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            if (!formData.name || !formData.blood_group || !formData.phone) {
                message.error('Please fill in all required fields');
                return;
            }

            const userId = await getuserdata('id');
            const donorData = {
                ...formData,
                blood_bank_id: userId
            };

            if (editingDonor) {
                await oneKlicks.update('', 'blood_donors', donorData, { id: editingDonor.id });
                message.success('Donor updated successfully');
            } else {
                await oneKlicks.create('', 'blood_donors', donorData);
                message.success('Donor added successfully');
            }

            setModalVisible(false);
            resetForm();
            loadDonors();
        } catch (error) {
            console.error('Error saving donor:', error);
            message.error('Failed to save donor');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            blood_group: '',
            phone: '',
            email: '',
            address: '',
            last_donation_date: '',
            status: 'active'
        });
        setEditingDonor(null);
    };

    const handleEdit = (donor) => {
        setEditingDonor(donor);
        setFormData({
            name: donor.name || '',
            blood_group: donor.blood_group || '',
            phone: donor.phone || '',
            email: donor.email || '',
            address: donor.address || '',
            last_donation_date: donor.last_donation_date || '',
            status: donor.status || 'active'
        });
        setModalVisible(true);
    };

    const handleDelete = async (donor) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete "${donor.name}"?`);
        if (confirmDelete) {
            try {
                await oneKlicks.delete('', 'blood_donors', { id: donor.id });
                message.success('Donor deleted successfully');
                loadDonors();
            } catch (error) {
                console.error('Error deleting donor:', error);
                message.error('Failed to delete donor');
            }
        }
    };

    const handleStatusToggle = async (donor) => {
        try {
            const newStatus = donor.status === 'active' ? 'inactive' : 'active';
            await oneKlicks.update('', 'blood_donors', { status: newStatus }, { id: donor.id });
            message.success(`Status changed to ${newStatus}`);
            loadDonors();
        } catch (error) {
            console.error('Error updating status:', error);
            message.error('Failed to update status');
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const getBloodGroupBadge = (group) => {
        const colors = {
            'A+': 'danger', 'A-': 'danger', 'B+': 'primary', 'B-': 'primary',
            'AB+': 'secondary', 'AB-': 'secondary', 'O+': 'success', 'O-': 'success'
        };
        return colors[group] || 'info';
    };

    return (
        <>
            <div className="container-fluid py-3">
                <div className="card mb-5">
                    <div className="card-body">
                        <div className="align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <h5 className="mb-0 font-weight-bold mr-3">Donors Management</h5>
                                <Badge bg="danger">Blood Donors</Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                                <InputGroup className="flex-grow-1 me-2 h-40">
                                    <Form.Control
                                        placeholder="Search by name, phone, blood group..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </InputGroup>
                                <Button className="btn-primary h-40" onClick={() => {
                                    resetForm();
                                    setModalVisible(true);
                                }}>
                                    <PlusOutlined /> Add New Donor
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Name</th>
                                        <th>Blood Group</th>
                                        <th>Contact</th>
                                        <th>Last Donation</th>
                                        <th>Status</th>
                                        <th style={{ width: '150px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">Loading...</td>
                                        </tr>
                                    ) : donors.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">No donors found</td>
                                        </tr>
                                    ) : (
                                        donors.map((item) => (
                                            <tr key={item.id}>
                                                <td data-label="ID">{item.id}</td>
                                                <td data-label="Name">
                                                    <div>
                                                        <strong>{item.name}</strong>
                                                        {item.email && (
                                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                                <MailOutlined className="me-1" />{item.email}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td data-label="Blood Group">
                                                    <Badge bg={getBloodGroupBadge(item.blood_group)}>
                                                        {item.blood_group}
                                                    </Badge>
                                                </td>
                                                <td data-label="Contact">
                                                    <PhoneOutlined className="me-1" />{item.phone}
                                                </td>
                                                <td data-label="Last Donation">
                                                    {item.last_donation_date || 'Never'}
                                                </td>
                                                <td data-label="Status">
                                                    <Button
                                                        onClick={() => handleStatusToggle(item)}
                                                        style={{
                                                            color: item.status === 'active' ? '#52c41a' : '#ff4d4f',
                                                            fontSize: '12px',
                                                            padding: '4px 12px'
                                                        }}
                                                    >
                                                        {item.status === 'active' ? 'Active' : 'Inactive'}
                                                    </Button>
                                                </td>
                                                <td data-label="Actions">
                                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                                        <Button
                                                            className="btn-info"
                                                            onClick={() => handleEdit(item)}
                                                            style={{ fontSize: '12px', padding: '4px 8px' }}
                                                        >
                                                            <EditOutlined />
                                                        </Button>
                                                        <Button
                                                            className="btn-danger"
                                                            onClick={() => handleDelete(item)}
                                                            style={{ fontSize: '12px', padding: '4px 8px' }}
                                                        >
                                                            <DeleteOutlined />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Paginationnew currentPage={page} totalPages={totalPages} handlePageChange={handlePageChange} />
                    </div>
                </div>

                {/* Add/Edit Modal */}
                <Modal
                    title={editingDonor ? 'Edit Donor' : 'Add New Donor'}
                    open={modalVisible}
                    onCancel={() => {
                        setModalVisible(false);
                        resetForm();
                    }}
                    onOk={handleSubmit}
                    width={600}
                >
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Full Name *</label>
                            <Form.Control
                                placeholder="Enter donor name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Blood Group *</label>
                            <Select
                                style={{ width: '100%' }}
                                placeholder="Select blood group"
                                value={formData.blood_group || undefined}
                                onChange={(value) => setFormData({ ...formData, blood_group: value })}
                                options={bloodGroups.map(g => ({ value: g, label: g }))}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Phone *</label>
                            <Form.Control
                                placeholder="Enter phone number"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Email</label>
                            <Form.Control
                                type="email"
                                placeholder="Enter email address"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>
                        <div className="col-12 mb-3">
                            <label className="form-label">Address</label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Enter address"
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Last Donation Date</label>
                            <Form.Control
                                type="date"
                                value={formData.last_donation_date}
                                onChange={(e) => setFormData({ ...formData, last_donation_date: e.target.value })}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Status</label>
                            <Select
                                style={{ width: '100%' }}
                                value={formData.status}
                                onChange={(value) => setFormData({ ...formData, status: value })}
                                options={[
                                    { value: 'active', label: 'Active' },
                                    { value: 'inactive', label: 'Inactive' }
                                ]}
                            />
                        </div>
                    </div>
                </Modal>
            </div>
        </>
    );
};

export default BloodDonors;
